// Ingenia Holiday Parks booking strategy.
//
// `site.reserveUrl` is the category's own page
// (`/our-parks/<state>/<park>/<category>?accommodationType=<site|cabin|glamping>`).
// The page reads the stay, the party and, for a pitch, the rig from its
// query string:
//
//   startDate=<yyyy-MM-dd>&endDate=<yyyy-MM-dd>&adults=<N>&children=0&infants=0&pets=0
//   [&equipmentType=caravan&equipmentSizeLength=<ft>&equipmentSizeUnit=ft]
//   [&equipmentType=tent&tentCapacity=<N>]
//
// With the stay it shows the total and "Book this stay", which goes to the
// rate step; a gone stay says "It looks like this stay isn't available on
// those dates" beside the date picker. A pitch's "Book this stay" stays
// disabled until a rig is entered, so the link carries the request's rig
// length as a caravan when it has one, and a tent for a tent pitch; an
// unsized request on any other pitch leaves the rig for the camper. The
// deeper `/booking/summary` rate page dead-ends on a gone stay, so it is
// never emitted (agencies/IngeniaHolidays/proof/discovery.md, Reserve URL).

import { format } from "date-fns";
import { resolveCheckout, resolveMinCapacity } from "./_context.js";

export default {
  buildBookingUrl(ctx) {
    const { site, arrivalDate, availabilityRequest } = ctx;
    const url = new URL(site.reserveUrl);
    if (!arrivalDate) return url.toString();

    const checkout = resolveCheckout(ctx);
    const adults = resolveMinCapacity(ctx);
    url.searchParams.set("startDate", format(arrivalDate, "yyyy-MM-dd"));
    url.searchParams.set("endDate", format(checkout, "yyyy-MM-dd"));
    url.searchParams.set("adults", String(adults));
    url.searchParams.set("children", "0");
    url.searchParams.set("infants", "0");
    url.searchParams.set("pets", "0");

    if (url.searchParams.get("accommodationType") === "site") {
      const length = availabilityRequest?.maxRVLength;
      if (length && site.type !== "tent") {
        url.searchParams.set("equipmentType", "caravan");
        url.searchParams.set("equipmentSizeLength", String(length));
        url.searchParams.set("equipmentSizeUnit", "ft");
      } else if (site.type === "tent") {
        url.searchParams.set("equipmentType", "tent");
        url.searchParams.set("tentCapacity", String(adults));
      }
    }
    return url.toString();
  },

  buildPreviewUrl(site) {
    // The park page: every category the park sells.
    const url = new URL(site.reserveUrl);
    url.search = "";
    url.pathname = url.pathname.split("/").slice(0, 4).join("/");
    return url.toString();
  },

  describeBookingInputs() {
    return {
      inputs: {
        arrivalDate: {
          status: "required",
          notes: "Encoded as &startDate=<yyyy-MM-dd>.",
        },
        departureDate: {
          status: "required",
          notes:
            "Encoded as &endDate=<yyyy-MM-dd>; resolved from arrival + minStayLength when not supplied.",
        },
        ref: {
          status: "ignored",
          notes: "site.reserveUrl is already the category's own page.",
        },
        partySize: {
          status: "defaulted",
          defaultValue: 1,
          notes:
            "Encoded as &adults=<N>; defaults to AvailabilityRequest.minCapacity, then 1.",
        },
        maxRVLength: {
          status: "optional",
          notes:
            "On a pitch, encoded as &equipmentType=caravan&equipmentSizeLength=<ft>&equipmentSizeUnit=ft; a tent pitch gets &equipmentType=tent&tentCapacity=<N>.",
        },
      },
    };
  },
};
